import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Bell,
  Mail,
  Database,
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Save,
  Send,
  BarChart3,
  FileDown,
  Eye,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL_FIXED = 'jeff@jeffpdental.com';

const StatusBadge = ({ status }) => {
  const config = {
    ok: { label: 'OK', className: 'bg-emerald-600 text-white hover:bg-emerald-600' },
    error: { label: 'Erreur', className: 'bg-red-600 text-white hover:bg-red-600' },
    warning: { label: 'Attention', className: 'bg-amber-500 text-white hover:bg-amber-500' },
  };
  const c = config[status] || config.warning;
  return <Badge className={c.className}>{c.label}</Badge>;
};

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [monitoringData, setMonitoringData] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [allRecipients, setAllRecipients] = useState([]);
  const [emailSelected, setEmailSelected] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);

  const [settings, setSettings] = useState({
    emailNotifications: true,
    notifyOnAlert: true,
    notifyOnNewClient: true,
  });

  const fetchSettings = useCallback(async () => {
    try {
      const [healthRes, emailRes, notifRes] = await Promise.all([
        axios.get(`${API}/health`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/email-status`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/notifications`).catch(() => ({ data: {} })),
      ]);
      setHealthStatus(healthRes.data);
      setEmailStatus(emailRes.data);
      const notifData = notifRes.data || {};
      setSettings({
        emailNotifications: notifData.email_enabled !== false,
        notifyOnAlert: notifData.notify_on_alert !== false,
        notifyOnNewClient: notifData.notify_on_new_client !== false,
      });
    } catch (e) {
      console.error('Erreur chargement settings:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const fetchMonitoring = async () => {
    setMonitoringLoading(true);
    try {
      const res = await axios.get(`${API}/monitoring/full`);
      setMonitoringData(res.data);
      toast.success('Monitoring actualisé');
    } catch {
      toast.error('Erreur lors du monitoring');
    } finally {
      setMonitoringLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/notifications`, {
        email: ADMIN_EMAIL_FIXED,
        email_enabled: settings.emailNotifications,
        notify_on_alert: settings.notifyOnAlert,
        notify_on_new_client: settings.notifyOnNewClient,
      });
      toast.success('Paramètres enregistrés');
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await axios.post(`${API}/settings/test-email`);
      if (res.data?.success) {
        toast.success(`Email de test envoyé à ${ADMIN_EMAIL_FIXED}`);
      } else {
        toast.error("Échec de l'envoi de l'email de test");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const downloadInstallGuide = async (lang = 'fr') => {
    try {
      const r = await axios.get(`${API}/guide/mobile-install.pdf`, {
        params: { lang },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = lang === 'fr'
        ? 'JPD-Link_installation_mobile_FR.pdf'
        : 'JPD-Link_installation_mobile_FR-EN.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Guide téléchargé');
    } catch {
      toast.error('Erreur de téléchargement');
    }
  };

  // Open the PDF inline in a new tab, generated as if sent to the chosen role.
  // The browser ships the httpOnly session cookie automatically (same-origin).
  const previewInstallGuide = async (role, lang = 'fr') => {
    try {
      const r = await axios.get(`${API}/guide/mobile-install.pdf`, {
        params: { lang, preview_role: role },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) toast.error('Le navigateur a bloqué la fenêtre — autorisez les pop-ups');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Erreur d\u2019aperçu');
    }
  };

  const fetchRecipients = async () => {
    try {
      const [clientsRes, labsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/labs`),
      ]);
      const clients = (clientsRes.data || [])
        .filter(c => c.active !== false && c.contact_email)
        .map(c => ({
          email: c.contact_email,
          name: c.client_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
          type: 'client',
          lang: 'fr',  // dentists are FR-speaking per current product
        }));
      const labs = (labsRes.data || [])
        .filter(l => l.active !== false && l.contact_email)
        .map(l => ({
          email: l.contact_email,
          name: l.lab_name,
          type: 'lab',
          lang: l.default_language === 'zh' ? 'en' : (l.default_language || 'en'),
        }));
      setAllRecipients([...clients, ...labs]);
    } catch (e) {
      console.error('fetchRecipients', e);
    }
  };

  const sendInstallGuideEmails = async () => {
    if (emailSelected.length === 0) return;
    setSendingEmails(true);
    try {
      const recipients = allRecipients
        .filter(r => emailSelected.includes(r.email))
        .map(r => ({ email: r.email, name: r.name, lang: r.lang }));
      const res = await axios.post(`${API}/admin/guide/send-email`, { recipients });
      const { sent = 0, total = 0, results = [] } = res.data || {};
      if (sent === total) {
        toast.success(`Guide envoyé à ${sent} destinataire${sent > 1 ? 's' : ''}`);
        setEmailSelected([]);
      } else if (sent > 0) {
        toast.warning(`${sent}/${total} envoyés — ${total - sent} échec(s). Vérifiez la config SendGrid.`);
      } else {
        const firstErr = results.find(r => !r.ok)?.error || 'Erreur inconnue';
        toast.error(`Échec : ${firstErr}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingEmails(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Paramètres" subtitle="Configuration et monitoring">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Paramètres" subtitle="Configuration et monitoring">
      <Tabs defaultValue="notifications" className="space-y-6">
        <TabsList>
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="monitoring" data-testid="tab-monitoring" className="flex items-center gap-2" onClick={fetchMonitoring}>
            <Activity className="h-4 w-4" />
            Monitoring
          </TabsTrigger>
          <TabsTrigger value="guides" data-testid="tab-guides" className="flex items-center gap-2" onClick={fetchRecipients}>
            <Smartphone className="h-4 w-4" />
            Guides
          </TabsTrigger>
        </TabsList>

        {/* ========= NOTIFICATIONS TAB ========= */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Canaux de notification
              </CardTitle>
              <CardDescription>
                Toutes les alertes administrateur sont envoyées à
                {' '}<strong>{ADMIN_EMAIL_FIXED}</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Notifications */}
              <div className="flex items-center justify-between p-4 border rounded-lg" data-testid="email-notif-section">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">
                      {emailStatus?.configured
                        ? `SendGrid configuré (${emailStatus.sender_email || 'expéditeur OK'})`
                        : 'SendGrid non configuré'}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="toggle-email"
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => setSettings({ ...settings, emailNotifications: checked })}
                  disabled={!emailStatus?.configured}
                />
              </div>

              {settings.emailNotifications && emailStatus?.configured && (
                <div className="ml-4 p-4 bg-muted/50 rounded-lg space-y-4">
                  <div className="space-y-2">
                    <Label>Email Admin destinataire</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        readOnly
                        value={ADMIN_EMAIL_FIXED}
                        className="bg-muted/30"
                        data-testid="admin-email-input"
                      />
                      <Button
                        variant="outline"
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                        data-testid="test-email-btn"
                      >
                        {testingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Adresse fixe (verrouillée). Cliquez sur le bouton pour envoyer un email de test.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Events to notify */}
          <Card>
            <CardHeader>
              <CardTitle>Événements à notifier</CardTitle>
              <CardDescription>Choisissez les événements pour lesquels vous voulez être alerté</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Alertes anti-désintermédiation</p>
                  <p className="text-sm text-muted-foreground">Quand un message suspect est détecté</p>
                </div>
                <Switch
                  data-testid="toggle-alert"
                  checked={settings.notifyOnAlert}
                  onCheckedChange={(checked) => setSettings({ ...settings, notifyOnAlert: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Nouveau client créé</p>
                  <p className="text-sm text-muted-foreground">Quand un nouveau cabinet est ajouté</p>
                </div>
                <Switch
                  data-testid="toggle-new-client"
                  checked={settings.notifyOnNewClient}
                  onCheckedChange={(checked) => setSettings({ ...settings, notifyOnNewClient: checked })}
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSaveNotifications} disabled={saving} className="w-full" data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer les paramètres
          </Button>
        </TabsContent>

        {/* ========= MONITORING TAB ========= */}
        <TabsContent value="monitoring" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    healthStatus?.status === 'healthy' ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {healthStatus?.status === 'healthy' ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">API Backend</p>
                    <p className="font-semibold" data-testid="api-status">
                      {healthStatus?.status === 'healthy' ? 'En ligne' : 'Hors ligne'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    healthStatus?.database === 'connected' ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    <Database className={`h-6 w-6 ${
                      healthStatus?.database === 'connected' ? 'text-emerald-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MongoDB</p>
                    <p className="font-semibold" data-testid="db-status">
                      {healthStatus?.database === 'connected' ? 'Connecté' : 'Déconnecté'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    emailStatus?.configured ? 'bg-emerald-100' : 'bg-amber-100'
                  }`}>
                    <Mail className={`h-6 w-6 ${
                      emailStatus?.configured ? 'text-emerald-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">SendGrid</p>
                    <p className="font-semibold" data-testid="email-service-status">
                      {emailStatus?.configured ? 'Configuré' : 'Non configuré'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Diagnostic complet
                </CardTitle>
                <CardDescription>Vérification détaillée de tous les services</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchMonitoring} disabled={monitoringLoading} data-testid="refresh-monitoring-btn">
                {monitoringLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Diagnostiquer
              </Button>
            </CardHeader>
            <CardContent>
              {!monitoringData ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Cliquez sur "Diagnostiquer" pour lancer une vérification complète</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid="monitor-database">
                    <div className="flex items-center gap-3">
                      <Database className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <span className="font-medium">Base de données MongoDB</span>
                        <p className="text-xs text-muted-foreground">{monitoringData.database?.message}</p>
                      </div>
                    </div>
                    <StatusBadge status={monitoringData.database?.status} />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid="monitor-email">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <span className="font-medium">Service Email (SendGrid)</span>
                        <p className="text-xs text-muted-foreground">{monitoringData.email?.message}</p>
                      </div>
                    </div>
                    <StatusBadge status={monitoringData.email?.status} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {monitoringData?.stats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistiques plateforme
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold" data-testid="stat-total-messages">{monitoringData.stats.total_messages}</p>
                    <p className="text-xs text-muted-foreground">Messages total</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold" data-testid="stat-messages-today">{monitoringData.stats.messages_today}</p>
                    <p className="text-xs text-muted-foreground">Messages aujourd'hui</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold" data-testid="stat-pending-alerts">{monitoringData.stats.pending_alerts}</p>
                    <p className="text-xs text-muted-foreground">Alertes en attente</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold" data-testid="stat-active-channels">{monitoringData.stats.active_channels}</p>
                    <p className="text-xs text-muted-foreground">Canaux actifs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========= GUIDES TAB ========= */}
        <TabsContent value="guides" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Guides d'installation mobile
              </CardTitle>
              <CardDescription>
                PDF expliquant aux clients et laboratoires comment installer
                l'application sur iPhone (iOS 16.4+) et Android (Chrome), activer
                les notifications push 🔔 et lire le badge de messages non lus.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-blue-900 mb-1">👁️ Aperçu — vérifiez avant d'envoyer</p>
                <p className="text-blue-800 text-xs mb-2">Le greeting change selon le destinataire : « Bonjour cher client » pour un client, « Bonjour cher partenaire laboratoire » pour un labo, « Bonjour Administrateur » quand c'est vous.</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => previewInstallGuide('client', 'fr')} data-testid="preview-guide-client">
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Aperçu version client
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => previewInstallGuide('lab', 'fr')} data-testid="preview-guide-lab">
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Aperçu version labo
                  </Button>
                </div>
              </div>

              <p className="text-sm font-medium">📥 Télécharger le PDF :</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-600 text-white hover:bg-blue-600">🇫🇷 FR</Badge>
                    <span className="font-medium">Version française</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Tout en français — à envoyer aux clients et laboratoires
                    francophones.
                  </p>
                  <Button onClick={() => downloadInstallGuide('fr')} className="w-full" data-testid="download-guide-fr">
                    <FileDown className="h-4 w-4 mr-2" />
                    Télécharger le PDF FR
                  </Button>
                </div>

                <div className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-600 text-white hover:bg-slate-600">🌍 FR + EN</Badge>
                    <span className="font-medium">Version bilingue</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Français + anglais dans le même document — pour les
                    utilisateurs internationaux.
                  </p>
                  <Button onClick={() => downloadInstallGuide('en')} variant="outline" className="w-full" data-testid="download-guide-bilingual">
                    <FileDown className="h-4 w-4 mr-2" />
                    Télécharger le PDF FR+EN
                  </Button>
                </div>
              </div>

              <div className="bg-muted/40 border border-border rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium">📱 Le guide couvre :</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>Installation sur iPhone / iPad — Safari → Partager → « Sur l'écran d'accueil »</li>
                  <li>Installation sur Android — Chrome → « Installer l'application »</li>
                  <li>Activation des notifications push via le bouton 🔔</li>
                  <li>Badge de messages non lus directement sur l'icône JPD</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* ----- Email send card ----- */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Envoyer le guide par email
              </CardTitle>
              <CardDescription>
                Sélectionnez un ou plusieurs clients / laboratoires : ils
                recevront le PDF en pièce jointe (langue adaptée à leur
                préférence enregistrée).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setEmailSelected(allRecipients.map(r => r.email))} data-testid="select-all-recipients">
                  Tout sélectionner
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEmailSelected([])} data-testid="deselect-all-recipients">
                  Tout désélectionner
                </Button>
                <span className="text-sm text-muted-foreground self-center ml-auto">
                  {emailSelected.length} / {allRecipients.length} sélectionné(s)
                </span>
              </div>

              <div className="border border-border rounded-lg max-h-72 overflow-y-auto" data-testid="recipients-list">
                {allRecipients.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">Aucun client/labo enregistré.</p>
                ) : allRecipients.map(r => (
                  <label
                    key={r.email}
                    className="flex items-center gap-3 px-3 py-2 border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={emailSelected.includes(r.email)}
                      onChange={e => {
                        if (e.target.checked) setEmailSelected([...emailSelected, r.email]);
                        else setEmailSelected(emailSelected.filter(x => x !== r.email));
                      }}
                      className="h-4 w-4"
                      data-testid={`recipient-${r.email}`}
                    />
                    <Badge variant="outline" className={r.type === 'client' ? 'bg-blue-50' : 'bg-amber-50'}>
                      {r.type === 'client' ? 'Client' : 'Labo'}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs uppercase">{r.lang}</Badge>
                  </label>
                ))}
              </div>

              <Button
                onClick={sendInstallGuideEmails}
                disabled={emailSelected.length === 0 || sendingEmails}
                className="w-full sm:w-auto"
                data-testid="send-install-guide-email"
              >
                {sendingEmails ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer le guide à {emailSelected.length} destinataire(s)
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default SettingsPage;
